// STEM Quest - Main Application
import './styles/index.css';
import './styles/components.css';
import gsap from 'gsap';
import { initializeApp } from './app';
import { showToast } from './utils/notifications';

// App State
const appState = {
  user: null,
  currentQuest: null,
  isAuthenticated: false,
  socketConnected: false,
  useBackend: false // Feature flag for backend usage
};

// Initialize the application
async function init() {
  try {
    console.log('🚀 Initializing STEM Quest...');

    // Check if backend is available
    const backendAvailable = import.meta.env.VITE_USE_BACKEND !== 'false';

    if (backendAvailable) {
      // Try to authenticate with backend
      const { authAPI } = await import('./services/api.js');

      if (authAPI.isAuthenticated()) {
        try {
          const response = await authAPI.getCurrentUser();
          if (response.success) {
            appState.user = response.data.user;
            appState.isAuthenticated = true;
            appState.useBackend = true;
            console.log('✅ Authenticated with backend');
          }
        } catch (error) {
          console.log('⚠️ Backend auth failed, will show login');
          appState.useBackend = true;
        }
      } else {
        appState.useBackend = true; // Backend available but not logged in
      }
    }

    // Fallback: If we tried to assume backend usage but it failed (e.g., server down),
    // OR if we are explicitly not using backend, try to load from localStorage.
    if (!appState.isAuthenticated) {
      const savedUser = localStorage.getItem('stem-quest-user');
      if (savedUser) {
        // If we found a local user but the backend failed, switch to local mode
        appState.user = JSON.parse(savedUser);
        appState.isAuthenticated = true;
        appState.useBackend = false; // Force local mode since backend failed/unreachable
        console.log('⚠️ Backend unreachable or not logged in. Falling back to localStorage mode.');
      }
    }

    // Fallback to localStorage ONLY if backend is NOT being used
    if (!appState.useBackend) {
      const savedUser = localStorage.getItem('stem-quest-user');
      if (savedUser) {
        appState.user = JSON.parse(savedUser);
        appState.isAuthenticated = true;
        console.log('✅ Using localStorage mode');
      }
    }

    // Hide loading  screen after a short delay
    setTimeout(() => {
      const loadingScreen = document.getElementById('loading-screen');
      if (loadingScreen) {
        loadingScreen.classList.add('hidden');
        setTimeout(() => loadingScreen.remove(), 500);
      }
    }, 1000);

    // Initialize the main app
    initializeApp(appState);

    // Show welcome message
    if (appState.user) {
      showToast(`Welcome back, ${appState.user.name || 'Explorer'}!`, 'success');
    }

  } catch (error) {
    console.error('Failed to initialize app:', error);
    showToast('Failed to load application. Please refresh.', 'error');
  }
}

// Start the app
init();

// Export app state for components
export { appState };
